import { createVisibleFlatColumns } from '../../components/header/utils'
import { COLUMNS_INDEX, COLUMNS_SELECTION, MIN_COLUMN_WIDTH, COLUMNS_RADIO, updateColumnsIndexWidth } from '../../config'
import { logError } from '../../utils'
import { isCellContentColumnWidth } from '../../plugins/cell-content-width'

// 生成索引和复选框的列配置
function createFixedColumnsOnLeft ({
  showIndex,
  showSelection,
  showRadio,
  commonColumns = {}
}) {
  const fixedColumnsOnLeft = []
  const obj = {
    level: 1,
    show: true,
    fixed: 'left',
    resizable: false,
  }
  if (showIndex) {
    fixedColumnsOnLeft.unshift({
      ...COLUMNS_INDEX,
      ...obj,
    })
  }
  if (showSelection) {
    const {
      selectionHeaderRenderToLeft,
      selectionHeaderRenderToRight,
      selectionRenderToLeft,
      selectionRenderToRight
    } = commonColumns
    fixedColumnsOnLeft.unshift({
      ...COLUMNS_SELECTION,
      ...obj,
      selectionHeaderRenderToLeft,
      selectionHeaderRenderToRight,
      selectionRenderToLeft,
      selectionRenderToRight
    })
  }
  if (showRadio) {
    fixedColumnsOnLeft.unshift({
      ...COLUMNS_RADIO,
      ...obj
    })
  }
  return fixedColumnsOnLeft
}

// 扁平化列配置
export const createFlatColumns = ({ columns, showIndex, showSelection, showRadio, commonColumns }) => {
  const addFixedConfInColumns = columns && columns.length > 0 ? createFixedColumnsOnLeft({ showIndex, showSelection, showRadio, commonColumns }) : []
  const { visibleFlatColumns, collapsedMap } = createVisibleFlatColumns([...addFixedConfInColumns, ...columns])

  return {
    collapsedMap,
    visibleFlatColumns,
  }
}

// 列配置的显示与否根据祖先节点的状态来决定
function validateColumnShow (acc, col) {
  const { parentNodeKey, key } = col
  if (acc.hasOwnProperty(key)) {
    const logConf = 'color: red;  font-weight: bold;'
    logError('The key of the configuration item cannot be unique ！！！！', logConf)
    logError(`表格列配置不允许出现重复的key（"${key}"）。请立即检查！！！`, logConf)
    logError('当前的列配置：', logConf)
    console.log({...col})
    logError('重复的列配置：', logConf)
    console.log({...acc[key]})
    console.log('\n')
  }
  const parentNode = acc[parentNodeKey]
  return parentNode && parentNode.show === false ? false : col.show
}

// 验证列配置的状态并纠正。 注意visibleFlatColumns一定是确保了同一层级的列会连续出现在结果中。 也就是按顺序拉平的结构。
function createColumnsKeyMap (visibleFlatColumns = []) {
  const columnKeyMap = visibleFlatColumns.reduce((acc, col) => {
    const { key } = col
    const show = validateColumnShow(acc, col)
    acc[key] = {
      ...col,
      show
    }
    return acc
  }, {})
  return columnKeyMap
}
function computedColumnsWidthInChildKeys (col, columnsKeyMap, collapsedMap) {
  const { width, children = [], childKeys = [] } = col || {}
  if (!children.length && width) {
    return width
  }
  return childKeys.reduce((acc, childKey) => {
    const { children = [], width, show } = columnsKeyMap[childKey] || {}
    // 仅计算未被折叠的宽度和
    if (!children.length && !collapsedMap[childKey] && show) {
      acc += width
    }
    return acc
  }, 0)
}

// 根据筛选的列和 折叠选项生成 多表格列配置
export function createColumns({ visibleFlatColumns, collapsedMap = {}, maxWidth, curMaxIndexVal, createFiltersMap } = {}) {
  const columnsKeyMap = createColumnsKeyMap(visibleFlatColumns)
  const columnVisibleConfIter = (key) => columnsKeyMap[key].show !== false && !collapsedMap[key]
  const fixedColumnsOnLeft = visibleFlatColumns.filter(column => (column.fixed === 'left' || column.fixed === true) && columnVisibleConfIter(column.key))
  const fixedColumnsOnRight = visibleFlatColumns.filter(column => column.fixed === 'right' && columnVisibleConfIter(column.key))
  const normalColumns = visibleFlatColumns.filter(column => !column.fixed && columnVisibleConfIter(column.key))
  if (curMaxIndexVal >= 10000) {
    updateColumnsIndexWidth(fixedColumnsOnLeft, curMaxIndexVal)
  }
  const mainColumns = [
    ...fixedColumnsOnLeft.map(i => {
      return Object.assign({}, i, {
        placeholderSign: true,
      })
    }),
    ...normalColumns,
    ...fixedColumnsOnRight.map(i => {
      return Object.assign({}, i, {
        placeholderSign: true
      })
    })
  ]

  const { width = 0 } = computedTableRealityWidth(mainColumns, maxWidth)
  const levelIter = col => col.level
  const maxLevel = Math.max(...mainColumns.map(levelIter), ...fixedColumnsOnLeft.map(levelIter), ...fixedColumnsOnRight.map(levelIter))

  function calculateOffsetXAndWidth (arr = [], iter) {
    let offsetX = 0
    return arr.map((col, index) => {
      const width = computedColumnsWidthInChildKeys(col, columnsKeyMap, collapsedMap)
      col.width = width
      if (index > 0) {
        const { width: preWidth, key: preKey } = mainColumns[index - 1]
        offsetX += preWidth
        // 如果上一次是父级，则减去父级的宽度。从父级的起始点开始
        if (col.parentNodeKey === preKey) {
          offsetX -= preWidth
        }
      }
      col.offsetX = offsetX
      const otherResult = iter(col, index) || {}
      return {
        ...col,
        originIndex: index,
        ...otherResult
      }
    })
  }
  const _mainColumns = calculateOffsetXAndWidth(mainColumns, function (col, index) {
    const { key } = col
    const fixedLeftKeys = ['_index', '_selection'].indexOf(key) > -1
    // 收集过滤的选项集合
    createFiltersMap(col)
    return {
      headerCellRender: fixedLeftKeys ? null : col.headerCellRender,
      rowCellRender: fixedLeftKeys ? null : col.rowCellRender,
    }
  })
  function setPlaceholderSign () {
    return {
      placeholderSign: false
    }
  }
  const _fixedColumnsOnLeft = calculateOffsetXAndWidth(fixedColumnsOnLeft.length ? mainColumns.slice(0, fixedColumnsOnLeft.length) : [], setPlaceholderSign)
  const _fixedColumnsOnRight = calculateOffsetXAndWidth(fixedColumnsOnRight.length ? mainColumns.slice(-fixedColumnsOnRight.length) : [], setPlaceholderSign)
  return {
    mainColumns: _mainColumns,
    fixedColumnsOnLeft: _fixedColumnsOnLeft,
    fixedColumnsOnRight: _fixedColumnsOnRight,
    maxLevel: !isFinite(maxLevel) ? 1 : maxLevel, // 确保不会报错，如果没有columns则 Match.max获取是 正负无穷
    width,
  }
}

function computedTableRealityWidth(mainColumns, maxWidth) {
  if (!maxWidth) return { width: 0 }
  const isVisible = column => !(column.children && column.children.length)
  const columnsVisibles = mainColumns.filter(isVisible)

  let { columnWidthSum, restColumns } = columnsVisibles.reduce((prev, column) => {
    const { width, _internal, _isAutoWidth } = column
    // 内部的宽不处理
    if (_internal) {
      prev.internalColumns.push(column)
      prev.columnWidthSum += width
    } else if (width && !_isAutoWidth) {
      if (!isCellContentColumnWidth(column, false)) {
        column._width = width
        prev.columnWidthSum += width
      }
    } else {
      prev.restColumns.push(column)
    }
    return prev
  }, {
    columnWidthSum: 0,
    restColumns: [],
    internalColumns: []
  })
  // 找出设置了width总和减去实际的tableWdith, 得到剩下可分配的width
  const restWidth = maxWidth - columnWidthSum
  if (restColumns.length) {
    // 剩下width平均分
    let averageWidth = Math.floor(restWidth / restColumns.length)
    // 最小宽度限制
    if (!averageWidth || averageWidth < MIN_COLUMN_WIDTH) {
      averageWidth = MIN_COLUMN_WIDTH
    }
    for (let i = 0; i < restColumns.length; i++) {
      const col = restColumns[i]
      if (!isCellContentColumnWidth(col)) {
        col.width = averageWidth
        col._isAutoWidth = true
        columnWidthSum += averageWidth
      }
    }
  }

  return {
    width: Math.min(maxWidth, columnWidthSum),
  }
}



// WEBPACK FOOTER //
// ./src/public/components/kye-table2/store/config/columns.js